import type { ApiClient } from '@tadpoles/api-client'
import type { MessagingMetadata } from '@tadpoles/common'

import { AsyncSingleFlight } from '../../utils/async-single-flight.js'

type BookmarkWriteRequest = {
    timestamp: number
    attemptVersion: number
    retry: boolean
}

type BookmarkWrite = {
    request: BookmarkWriteRequest
    completion: Promise<boolean>
}

type BookmarkSynchronizationOptions = {
    eligibleSubjectKeys: () => ReadonlySet<string>
    getMetadata: (subjectKey: string) => MessagingMetadata | undefined
    setMetadata: (subjectKey: string, metadata: MessagingMetadata | undefined) => void
    onChange: () => void
}

/** Owns optimistic bookmarks and their reconciliation with server metadata. */
export class BookmarkSynchronizer {
    readonly #api: Pick<ApiClient['messaging'], 'bookmark' | 'getMetadata'>
    readonly #options: BookmarkSynchronizationOptions
    readonly #metadataRequest = new AsyncSingleFlight()
    readonly #operationController = new AbortController()
    #destroyed = false
    readonly #bookmarkVersions = new Map<string, number>()
    readonly #bookmarkWrites = new Map<string, BookmarkWrite>()
    readonly #nextBookmarkWrites = new Map<string, BookmarkWrite>()
    readonly #confirmedBookmarks = new Map<string, number>()
    readonly #optimisticBookmarks = new Map<string, number>()
    readonly #pendingBookmarks = new Map<string, number>()

    public constructor(
        api: Pick<ApiClient['messaging'], 'bookmark' | 'getMetadata'>,
        options: BookmarkSynchronizationOptions
    ) {
        this.#api = api
        this.#options = options
    }

    public async refreshMetadata(): Promise<void> {
        this.#assertAvailable()
        await this.#metadataRequest.run(async () => {
            this.#assertAvailable()
            await this.#loadMetadata()
        })
    }

    public retainEligible(eligibleSubjectKeys: ReadonlySet<string>): void {
        // Keep active writes until they settle so a restored subject cannot send out of order.
        for (const values of [
            this.#nextBookmarkWrites,
            this.#pendingBookmarks,
            this.#confirmedBookmarks,
            this.#optimisticBookmarks
        ]) {
            for (const subjectKey of values.keys()) {
                if (!eligibleSubjectKeys.has(subjectKey)) values.delete(subjectKey)
            }
        }
        // A restored subject must not reuse the version of its still-active request.
        for (const subjectKey of this.#bookmarkVersions.keys()) {
            if (!eligibleSubjectKeys.has(subjectKey) && !this.#bookmarkWrites.has(subjectKey)) {
                this.#bookmarkVersions.delete(subjectKey)
            }
        }
    }

    public destroy(): void {
        this.#destroyed = true
        this.#operationController.abort()
        this.#bookmarkVersions.clear()
        this.#bookmarkWrites.clear()
        this.#nextBookmarkWrites.clear()
        this.#confirmedBookmarks.clear()
        this.#optimisticBookmarks.clear()
        this.#pendingBookmarks.clear()
    }

    public async advance(subjectKey: string, timestamp: number): Promise<boolean> {
        this.#assertAvailable()

        if (!this.#options.eligibleSubjectKeys().has(subjectKey)) {
            throw new Error('Messaging requires an accessible messaging-enabled child.')
        }

        if (!Number.isFinite(timestamp)) {
            throw new Error('Messaging bookmark timestamps must be finite millis.')
        }

        const metadata = this.#options.getMetadata(subjectKey)
        const currentTimestamp = metadata?.bookmarkTimestamp
        if (currentTimestamp !== undefined && currentTimestamp >= timestamp) {
            const active = this.#bookmarkWrites.get(subjectKey)
            if (active && active.request.timestamp >= timestamp) {
                return await active.completion
            }
            const next = this.#nextBookmarkWrites.get(subjectKey)
            if (next && next.request.timestamp >= timestamp) {
                return await next.completion
            }
            return true
        }

        const lastUnreadMessage = metadata?.lastUnreadMessage
        this.#bookmarkVersions.set(subjectKey, (this.#bookmarkVersions.get(subjectKey) ?? 0) + 1)
        this.#optimisticBookmarks.set(
            subjectKey,
            Math.max(timestamp, this.#optimisticBookmarks.get(subjectKey) ?? timestamp)
        )
        this.#options.setMetadata(subjectKey, {
            ...metadata,
            subjectKey,
            bookmarkTimestamp: timestamp,
            lastUnreadMessage:
                lastUnreadMessage && lastUnreadMessage.captureTimestamp > timestamp
                    ? lastUnreadMessage
                    : undefined
        })
        this.#options.onChange()

        const attemptVersion = this.#bookmarkVersions.get(subjectKey) ?? 0
        return await this.#requestBookmarkWrite(subjectKey, timestamp, attemptVersion, false)
    }

    async #loadMetadata(): Promise<void> {
        await this.#retryPendingBookmarks()
        this.#assertAvailable()
        const bookmarkVersionsAtRequest = new Map(this.#bookmarkVersions)
        const payload = await this.#api.getMetadata({
            signal: this.#operationController.signal
        })
        this.#assertAvailable()
        const eligibleSubjectKeys = this.#options.eligibleSubjectKeys()
        const metadataBySubject = new Map(
            payload.metadata
                .filter(({ subjectKey }) => eligibleSubjectKeys.has(subjectKey))
                .map((metadata) => [metadata.subjectKey, metadata])
        )

        for (const subjectKey of eligibleSubjectKeys) {
            const existing = this.#options.getMetadata(subjectKey)
            this.#options.setMetadata(
                subjectKey,
                this.#reconcileMetadataResponse(
                    metadataBySubject.get(subjectKey),
                    existing,
                    bookmarkVersionsAtRequest.get(subjectKey) ?? 0
                )
            )
        }

        for (const subjectKey of metadataBySubject.keys()) {
            if (
                (this.#bookmarkVersions.get(subjectKey) ?? 0) ===
                (bookmarkVersionsAtRequest.get(subjectKey) ?? 0)
            ) {
                this.#pendingBookmarks.delete(subjectKey)
            }
        }
        this.#options.onChange()
    }

    async #retryPendingBookmarks(): Promise<void> {
        const operations: Promise<boolean>[] = []
        for (const [subjectKey, timestamp] of [...this.#pendingBookmarks]) {
            if (!this.#options.eligibleSubjectKeys().has(subjectKey)) {
                this.#pendingBookmarks.delete(subjectKey)
                continue
            }

            const currentBoundary = this.#options.getMetadata(subjectKey)?.bookmarkTimestamp
            const retryTimestamp = Math.max(timestamp, currentBoundary ?? timestamp)
            const attemptVersion = this.#bookmarkVersions.get(subjectKey) ?? 0
            operations.push(
                this.#requestBookmarkWrite(subjectKey, retryTimestamp, attemptVersion, true)
            )
        }
        await Promise.all(operations)
    }

    #requestBookmarkWrite(
        subjectKey: string,
        timestamp: number,
        attemptVersion: number,
        retry: boolean
    ): Promise<boolean> {
        const active = this.#bookmarkWrites.get(subjectKey)
        if (active && timestamp <= active.request.timestamp) {
            return active.completion
        }

        const next = this.#nextBookmarkWrites.get(subjectKey)
        if (next) {
            if (timestamp >= next.request.timestamp) {
                next.request = { timestamp, attemptVersion, retry }
            }
            return next.completion
        }

        const request = { timestamp, attemptVersion, retry }
        if (!active) {
            return this.#startBookmarkWrite(subjectKey, request)
        }

        const completion: Promise<boolean> = active.completion.then(
            () => this.#flushNextBookmarkWrite(subjectKey, completion),
            () => this.#flushNextBookmarkWrite(subjectKey, completion)
        )
        this.#nextBookmarkWrites.set(subjectKey, { request, completion })
        return completion
    }

    #flushNextBookmarkWrite(subjectKey: string, completion: Promise<boolean>): Promise<boolean> {
        const next = this.#nextBookmarkWrites.get(subjectKey)
        if (!next || next.completion !== completion) {
            return Promise.resolve(false)
        }
        this.#nextBookmarkWrites.delete(subjectKey)
        return this.#startBookmarkWrite(subjectKey, next.request)
    }

    #startBookmarkWrite(subjectKey: string, request: BookmarkWriteRequest): Promise<boolean> {
        const completion = this.#performBookmarkWrite(
            subjectKey,
            request.timestamp,
            request.attemptVersion,
            request.retry
        ).finally(() => {
            if (this.#bookmarkWrites.get(subjectKey)?.completion === completion) {
                this.#bookmarkWrites.delete(subjectKey)
                if (!this.#destroyed && !this.#options.eligibleSubjectKeys().has(subjectKey)) {
                    this.#bookmarkVersions.delete(subjectKey)
                }
            }
        })
        this.#bookmarkWrites.set(subjectKey, { request, completion })
        return completion
    }

    async #performBookmarkWrite(
        subjectKey: string,
        timestamp: number,
        attemptVersion: number,
        retry: boolean
    ): Promise<boolean> {
        if (this.#destroyed || !this.#options.eligibleSubjectKeys().has(subjectKey)) {
            return false
        }

        try {
            await this.#api.bookmark(subjectKey, timestamp, {
                signal: this.#operationController.signal
            })
            if (this.#destroyed) {
                return false
            }
            if (!this.#options.eligibleSubjectKeys().has(subjectKey)) {
                return true
            }
            this.#recordConfirmedBookmark(subjectKey, timestamp)
            if (this.#finishCurrentBookmarkAttempt(subjectKey, attemptVersion)) {
                this.#optimisticBookmarks.delete(subjectKey)
            }
            return true
        } catch (error) {
            if (this.#operationController.signal.aborted) {
                return false
            }
            if (!this.#options.eligibleSubjectKeys().has(subjectKey)) {
                return false
            }

            if (this.#finishCurrentBookmarkAttempt(subjectKey, attemptVersion)) {
                this.#optimisticBookmarks.delete(subjectKey)
                const currentBoundary =
                    this.#options.getMetadata(subjectKey)?.bookmarkTimestamp ?? timestamp
                const pendingTimestamp = this.#pendingBookmarks.get(subjectKey) ?? timestamp
                this.#pendingBookmarks.set(
                    subjectKey,
                    Math.max(timestamp, currentBoundary, pendingTimestamp)
                )
            }
            console.warn(
                retry
                    ? 'Unable to retry messaging bookmark synchronization.'
                    : 'Unable to synchronize messaging bookmark.',
                error
            )
            return false
        }
    }

    #recordConfirmedBookmark(subjectKey: string, timestamp: number): void {
        const confirmedTimestamp = Math.max(
            timestamp,
            this.#confirmedBookmarks.get(subjectKey) ?? timestamp
        )
        this.#confirmedBookmarks.set(subjectKey, confirmedTimestamp)
        const pendingTimestamp = this.#pendingBookmarks.get(subjectKey)
        if (pendingTimestamp !== undefined && pendingTimestamp <= confirmedTimestamp) {
            this.#pendingBookmarks.delete(subjectKey)
        }
    }

    #finishCurrentBookmarkAttempt(subjectKey: string, attemptVersion: number): boolean {
        if ((this.#bookmarkVersions.get(subjectKey) ?? 0) !== attemptVersion) {
            return false
        }

        this.#bookmarkVersions.set(subjectKey, attemptVersion + 1)
        return true
    }

    #reconcileMetadataResponse(
        incoming: MessagingMetadata | undefined,
        existing: MessagingMetadata | undefined,
        bookmarkVersionAtRequest: number
    ): MessagingMetadata | undefined {
        const subjectKey = existing?.subjectKey ?? incoming?.subjectKey
        const bookmarkChangedAfterRequest =
            (this.#bookmarkVersions.get(subjectKey ?? '') ?? 0) !== bookmarkVersionAtRequest
        const confirmedBookmark = subjectKey ? this.#confirmedBookmarks.get(subjectKey) : undefined
        const optimisticBookmark = subjectKey
            ? this.#optimisticBookmarks.get(subjectKey)
            : undefined
        const responseConfirmsBookmark =
            confirmedBookmark !== undefined &&
            incoming?.bookmarkTimestamp !== undefined &&
            incoming.bookmarkTimestamp >= confirmedBookmark
        if (responseConfirmsBookmark && subjectKey) {
            this.#confirmedBookmarks.delete(subjectKey)
        }
        const responseRegressesConfirmedBookmark =
            confirmedBookmark !== undefined && !responseConfirmsBookmark
        const responseRegressesOptimisticBookmark =
            optimisticBookmark !== undefined &&
            (incoming?.bookmarkTimestamp === undefined ||
                incoming.bookmarkTimestamp < optimisticBookmark)
        if (
            (!bookmarkChangedAfterRequest &&
                !responseRegressesConfirmedBookmark &&
                !responseRegressesOptimisticBookmark) ||
            !existing
        ) {
            return incoming
        }

        if (!incoming) {
            return existing
        }

        const bookmarkTimestamp = existing.bookmarkTimestamp
        return {
            ...incoming,
            bookmarkTimestamp,
            lastUnreadMessage:
                incoming.lastUnreadMessage &&
                (bookmarkTimestamp === undefined ||
                    incoming.lastUnreadMessage.captureTimestamp > bookmarkTimestamp)
                    ? incoming.lastUnreadMessage
                    : undefined
        }
    }

    #assertAvailable(): void {
        if (this.#destroyed) {
            throw new Error('Messaging service has been destroyed.')
        }
    }
}
